// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Box, Button, Divider, Grid, Group, Modal, Select, Stack, Textarea, TextInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { createReference, normalizeErrorString } from '@medplum/core';
import type { CodeableConcept, Extension, Patient, Practitioner, Reference, Task } from '@medplum/fhirtypes';
import { ResourceInput, useMedplum, useMedplumProfile } from '@medplum/react';
import { IconCircleCheck, IconCircleOff } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
  getTaskAssociatedActionCode,
  TASK_ASSOCIATED_ACTION_URL,
  TASK_ASSOCIATED_ACTIONS,
  type TaskAssociatedActionCode,
} from '../../constants/task-associated-action';
import { withTenantScope } from '../../utils/tenant';
import { ProviderSelect } from './ProviderSelect';

export interface NewTaskModalProps {
  opened: boolean;
  onClose: () => void;
  onTaskCreated?: (task: Task) => void;
  /** When provided, the modal opens in edit mode for this task. */
  task?: Task;
  /** Called after a successful edit save. */
  onTaskUpdated?: (task: Task) => void;
}

const ASSOCIATED_ACTION_OPTIONS = TASK_ASSOCIATED_ACTIONS.map(({ code, label }) => ({ value: code, label }));

const URGENCY_OPTIONS = [
  { value: 'routine', label: 'Routine' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'asap', label: 'ASAP' },
  { value: 'stat', label: 'STAT' },
];

export function NewTaskModal(props: NewTaskModalProps): JSX.Element {
  const { opened, onClose, onTaskCreated, task: existingTask, onTaskUpdated } = props;
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const isEdit = Boolean(existingTask);

  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [priority, setPriority] = useState<string>('routine');
  const [assignee, setAssignee] = useState<Reference<Practitioner> | undefined>();
  const [dueDate, setDueDate] = useState<string | undefined>();
  const [taskPatient, setTaskPatient] = useState<Reference<Patient> | undefined>();
  const [associatedAction, setAssociatedAction] = useState<TaskAssociatedActionCode | undefined>();

  // Internal state preserved from existing implementation; not exposed in the redesigned form.
  const [status, setStatus] = useState<Task['status']>('draft');
  const [performerType, setPerformerType] = useState<CodeableConcept | undefined>();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const initialState = useMemo(() => {
    if (!existingTask) {
      return null;
    }
    return {
      title: existingTask.code?.text ?? existingTask.description ?? '',
      description: existingTask.description ?? '',
      priority: existingTask.priority ?? 'routine',
      assignee: existingTask.owner as Reference<Practitioner> | undefined,
      dueDate: existingTask.restriction?.period?.end,
      taskPatient: existingTask.for as Reference<Patient> | undefined,
      associatedAction: getTaskAssociatedActionCode(existingTask),
      status: existingTask.status ?? 'draft',
      performerType: existingTask.performerType?.[0],
    };
  }, [existingTask]);

  // Re-hydrate state every time the modal opens, so re-opening with a different task picks up fresh values.
  useEffect(() => {
    if (!opened) {
      return;
    }
    if (initialState) {
      setTitle(initialState.title);
      setDescription(initialState.description);
      setPriority(initialState.priority);
      setAssignee(initialState.assignee);
      setDueDate(initialState.dueDate);
      setTaskPatient(initialState.taskPatient);
      setAssociatedAction(initialState.associatedAction);
      setStatus(initialState.status);
      setPerformerType(initialState.performerType);
    } else {
      setTitle('');
      setDescription('');
      setPriority('routine');
      setAssignee(undefined);
      setDueDate(undefined);
      setTaskPatient(undefined);
      setAssociatedAction(undefined);
      setStatus('draft');
      setPerformerType(undefined);
    }
    setIsSubmitting(false);
  }, [opened, initialState]);

  const buildExtensions = (): Extension[] | undefined => {
    const others = (existingTask?.extension ?? []).filter((e) => e.url !== TASK_ASSOCIATED_ACTION_URL);
    const next: Extension[] = [...others];
    if (associatedAction) {
      next.push({ url: TASK_ASSOCIATED_ACTION_URL, valueCode: associatedAction });
    }
    return next.length > 0 ? next : undefined;
  };

  const handleSubmit = async (): Promise<void> => {
    if (!title.trim()) {
      notifications.show({
        color: 'red',
        icon: <IconCircleOff />,
        title: 'Validation Error',
        message: 'Task title is required',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const baseFields: Partial<Task> = {
        status,
        priority: priority as Task['priority'],
        code: { text: title },
        description: description.trim() || undefined,
        for: taskPatient,
        owner: assignee,
        performerType: performerType ? [performerType] : undefined,
        restriction: dueDate ? { period: { end: dueDate } } : undefined,
        extension: buildExtensions(),
      };

      if (isEdit && existingTask) {
        const updated: Task = {
          ...existingTask,
          ...baseFields,
        };
        const saved = await medplum.updateResource(withTenantScope(updated));
        notifications.show({ icon: <IconCircleCheck />, title: 'Success', message: 'Task updated' });
        onTaskUpdated?.(saved);
        onClose();
        return;
      }

      const newTask: Task = {
        resourceType: 'Task',
        intent: 'order',
        authoredOn: new Date().toISOString(),
        requester: profile ? createReference(profile) : undefined,
        ...baseFields,
      } as Task;

      const created = await medplum.createResource(withTenantScope(newTask));
      notifications.show({ icon: <IconCircleCheck />, title: 'Success', message: 'Task created successfully' });
      onTaskCreated?.(created);
      onClose();
    } catch (error) {
      notifications.show({
        color: 'red',
        icon: <IconCircleOff />,
        title: 'Error',
        message: normalizeErrorString(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="xl"
      title={isEdit ? 'Edit Task' : 'Create a Task'}
      centered
      withCloseButton={false}
      radius="lg"
      styles={{
        header: { justifyContent: 'center', paddingBottom: 0 },
        title: { fontSize: '1.5rem', fontWeight: 700 },
      }}
    >
      <Stack gap={0}>
        <Box bg="gray.0" m="md" p="md" style={{ borderRadius: 20 }}>
          <Grid>
            <Grid.Col span={6}>
              <Stack gap="md">
                <TextInput
                  label="Task Title"
                  placeholder="Enter task title"
                  value={title}
                  onChange={(event) => setTitle(event.currentTarget.value)}
                />

                <Select
                  label="Associated Action"
                  placeholder="Select an action (optional)"
                  data={ASSOCIATED_ACTION_OPTIONS}
                  value={associatedAction ?? null}
                  onChange={(value) => setAssociatedAction((value as TaskAssociatedActionCode) || undefined)}
                  clearable
                />

                <Select
                  label="Urgency"
                  placeholder="Select urgency"
                  data={URGENCY_OPTIONS}
                  value={priority}
                  onChange={(value) => setPriority(value || 'routine')}
                  allowDeselect={false}
                  required
                />
              </Stack>
            </Grid.Col>

            <Grid.Col span={6}>
              <Stack gap="md">
                <ProviderSelect
                  name="assignee"
                  label="Assign to"
                  placeholder="Select assignee"
                  required
                  value={assignee}
                  onChange={setAssignee}
                />

                <ResourceInput<Patient>
                  resourceType="Patient"
                  name="patient"
                  label="Patient"
                  placeholder="Search patients"
                  defaultValue={taskPatient as unknown as Patient | undefined}
                  onChange={(value) => setTaskPatient(value ? createReference(value) : undefined)}
                />

                <DatePickerInput
                  label="Due Date"
                  placeholder="Select due date"
                  value={dueDate ? new Date(dueDate) : null}
                  onChange={(value) => {
                    if (!value) {
                      setDueDate(undefined);
                      return;
                    }
                    const d = typeof value === 'string' ? new Date(value) : value;
                    // End-of-day local time so a task isn't flagged overdue at 12:00am of the due date.
                    d.setHours(23, 59, 59, 999);
                    setDueDate(d.toISOString());
                  }}
                  clearable
                />
              </Stack>
            </Grid.Col>

            <Grid.Col span={12}>
              <Textarea
                label="Description"
                placeholder="Enter task description (optional)"
                value={description}
                onChange={(event) => setDescription(event.currentTarget.value)}
                minRows={3}
                autosize
                maxRows={6}
              />
            </Grid.Col>
          </Grid>
        </Box>

        <Divider />

        <Group justify="center" p="md">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={isSubmitting}>
            {isEdit ? 'Save' : 'Finish'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
